import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Form from '@components/Form';
import Modal from '@components/Modal';
import { Show, ShowFirstMatching } from '@components/Show';
import { Stepper } from '@components/Stepper';
import { useEntityStore } from '@ContractBuilder/store';
import { useRenewalStore } from '@ContractBuilder/store/renewal.store';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { createRenewal } from '@mutations/createRenewal';
import { invalidateSavedViewCounts } from '@queries/fetchSavedViews';
import { submissionQueries } from '@queries/fetchSubmissions';
import Nav from '@src/routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { omit } from 'lodash-es';

import { useCreateRenewalForm } from '../hooks/useCreateRenewalForm';
import { useMergeEndorsements } from '../hooks/useMergeEndorsements';
import { useStepper } from '../hooks/useStepper';
import { useTemplatesData } from '../hooks/useTemplatesData';
import type { CreateRenewalFormState, StepProps } from '../types';
import { Step } from '../types';
import { FormFooter } from '../views/FormFooter';
import { LoadingSkeleton } from '../views/LoadingSkeleton';
import { MergeEndorsementsSection, MergeEndorsementsUnavailableNotice } from '../views/MergeEndorsementsSection';
import { Step0 } from '../views/Step0';
import { Step0OpenSearch } from '../views/Step0OpenSearch';
import { Step1 } from '../views/Step1';
import { Step2 } from '../views/Step2';
import { Step3 } from '../views/Step3';

interface CreateRenewalModalControllerProps {
  handleClose: () => void;
  isStandalone?: boolean;
}

export const CreateRenewalModalController: FC<CreateRenewalModalControllerProps> = ({
  handleClose,
  isStandalone = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storeSubmission = useEntityStore(({ submission }) => submission);
  const { setRenewalId, setMovedBlockIds } = useRenewalStore(({ setRenewalId, setMovedBlockIds }) => ({
    setRenewalId,
    setMovedBlockIds,
  }));
  const getFeatureFlag = useFeatureFlags();
  const isOpenSearchEnabled = getFeatureFlag('OpenSearch');

  const submissionId = useParams().id;

  const { control, formValues, handleSubmit, setValue } = useCreateRenewalForm(storeSubmission?.name);

  const { currentStep, getStepperItemState, onNext, onPrevious } = useStepper({
    initialStep: isStandalone ? Step.PreSelect : Step.Base,
    onCancel: handleClose,
  });

  // Lazy fetch submission in standalone mode when advancing to Step 1
  const { data: standaloneSubmission, isLoading: isFetchingStandaloneSubmission } = useQuery({
    ...submissionQueries.detail(formValues.renewFromSubmissionId as string),
    enabled: isStandalone && !!formValues.renewFromSubmissionId && currentStep === Step.Base,
  });

  useEffect(() => {
    if (standaloneSubmission?.name) {
      setValue('name', standaloneSubmission.name);
    }
  }, [standaloneSubmission?.name, setValue]);

  const submission = isStandalone ? standaloneSubmission : storeSubmission;
  const { templates, canRefreshCurrentTemplate, isFetchingTemplates } = useTemplatesData(submission);

  const {
    sectionProps: mergeSectionProps,
    hasEndorsementsError,
    isFetchingEndorsements,
    mergePayload,
  } = useMergeEndorsements({
    renewedContractId: isStandalone ? formValues.renewFromSubmissionId : submissionId,
    value: formValues.mergeEndorsements,
    setValue,
  });

  // Set default renewal type on initial load
  useEffect(() => {
    if (formValues.type !== undefined || !submission) {
      return;
    }

    const defaultType = canRefreshCurrentTemplate ? 'refresh' : 'replicate';
    setValue('type', defaultType);
  }, [canRefreshCurrentTemplate, formValues.type, submission, setValue]);

  // Update baseTemplateId when submission changes and type is 'refresh'
  useEffect(() => {
    if (!submission || formValues.type !== 'refresh') {
      return;
    }

    if (canRefreshCurrentTemplate && submission.sourceResourceId) {
      setValue('baseTemplateId', submission.sourceResourceId);
    }
  }, [canRefreshCurrentTemplate, formValues.type, submission, setValue]);

  const [isCreatingRenewal, setIsCreatingRenewal] = useState(false);
  const isLoading = isFetchingStandaloneSubmission || isFetchingEndorsements || isCreatingRenewal;
  const isAdvanceBlocked =
    isFetchingStandaloneSubmission || isCreatingRenewal || (isFetchingEndorsements && currentStep !== Step.PreSelect);

  const handleCreateRenewal = async (values: CreateRenewalFormState) => {
    setIsCreatingRenewal(true);

    try {
      const response = await createRenewal(
        {
          ...omit(values, 'type', 'renewFromSubmissionId', 'mergeEndorsements'),
          ...(mergePayload && { mergeEndorsements: mergePayload }),
        },
        isStandalone ? (values.renewFromSubmissionId as string) : (submissionId as string),
      );

      const renewalContractPath = generatePath(Nav.SubmissionContract, {
        id: response.id,
      });

      setRenewalId(response.id);
      setMovedBlockIds(response.movedBlockIds);
      invalidateSavedViewCounts(queryClient);

      return navigate(renewalContractPath);
    } finally {
      setIsCreatingRenewal(false);
    }
  };

  const stepProps: StepProps = {
    canRefreshCurrentTemplate,
    control,
    formValues,
    isLoading,
    submission,
    step: currentStep,
    templates,
    setValue,
  };

  const isPreSelectionStep = currentStep === Step.PreSelect;
  const isFirstStep = currentStep === Step.Base;
  const isSecondStep = currentStep === Step.Type;

  return (
    <Modal
      className="w-[572px] gap-0 sm:max-w-[572px]"
      onClose={handleClose}
      open
      shouldShowCloseIcon={false}
      title="Create a renewal contract"
      titleClassName="pt-4 text-lg leading-6 font-medium justify-center!"
    >
      <Form
        className={clsx('flex', 'min-w-0', 'flex-col', 'items-center justify-center', 'gap-6', 'px-8 pt-0 pb-6')}
        onSubmit={handleSubmit(handleCreateRenewal)}
        onKeyDown={preventDefaultEnterSubmit}
      >
        <p className="text-sm leading-5 font-normal text-gray-500">
          Please fill in the following information to setup a renewal
        </p>
        <Stepper getStepperItemState={getStepperItemState} size={isStandalone ? 4 : 3} />
        <ShowFirstMatching>
          <Show when={isFetchingTemplates || isFetchingStandaloneSubmission}>
            <LoadingSkeleton />
          </Show>
          <Show when={true}>
            <>
              <ShowFirstMatching>
                <Show when={isPreSelectionStep}>
                  {isOpenSearchEnabled ? <Step0OpenSearch {...stepProps} /> : <Step0 {...stepProps} />}
                </Show>
                <Show when={isFirstStep}>
                  <>
                    <Step1 {...stepProps} />
                    {mergeSectionProps && <MergeEndorsementsSection {...mergeSectionProps} />}
                    {hasEndorsementsError && <MergeEndorsementsUnavailableNotice />}
                  </>
                </Show>
                <Show when={isSecondStep}>
                  <Step2 {...stepProps} />
                </Show>
                <Show when={true}>
                  <Step3 {...stepProps} />
                </Show>
              </ShowFirstMatching>
              <FormFooter
                {...stepProps}
                isLoading={isAdvanceBlocked}
                isSubmitting={isCreatingRenewal}
                onClose={handleClose}
                onNext={onNext}
                onPrevious={onPrevious}
              />
            </>
          </Show>
        </ShowFirstMatching>
      </Form>
    </Modal>
  );
};
